import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { formatISOTimeForDisplay } from '@plato/shared-util/time-utils';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { Card } from '@plato/ui-legacy/components/ui/card';
import { InfoTag } from '@plato/ui-legacy/components/ui/info-tag';
import { Text } from '@plato/ui-legacy/components/ui/text';
import { cn } from '@plato/ui/util/classes';
import { formatISO, isSameDay } from 'date-fns';
import { CircleCheck, MapPinIcon, RefreshCwIcon, Store } from 'lucide-react';
import { forwardRef, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { GetManyEventsListItem } from '../../../server/types/infer-trpc-types.ts';
import { api } from '../../../utils/api.ts';
import { getEventMetadata } from '../../../utils/event-utils.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { EditNoteDialog } from '../../notes/components/edit-note-dialog.tsx';
import { LogInteractionDialog } from '../../notes/components/log-interaction-dialog.tsx';
import { EventDetailsDialog } from './event-details-dialog.tsx';
import { EventLogButton } from './event-log-button.tsx';

type Props = {
    className?: string;
    date: string;
    event: GetManyEventsListItem;
};

export const EventCard = forwardRef<HTMLDivElement, Props>(({ className, date, event }, ref) => {
    const { t } = useTranslation('events');
    const sendEvent = useAnalytics();
    const apiUtils = api.useUtils();
    const { userId } = useAppConfig();

    const handleClick = (): void => {
        void DialogStateManager.show(EventDetailsDialog, {
            eventId: event.event_id,
            date,
            analyticsSource: 'calendar',
        });
        sendEvent('event_clicked', getEventMetadata(event));
    };

    const recurrenceWeeksCount = event.recurrenceRule?.weeks ?? 0;
    const isOwner = event.owner_id === userId;
    const isFutureDate = date > formatISO(new Date(), { representation: 'date' });
    // STEER-252: non-owners see the logged state too
    const showLogButton = !isFutureDate && (isOwner || event.notes.length > 0);

    const handleButtonClick = (e: MouseEvent<HTMLButtonElement>): void => {
        // don't bubble up to the card onClick handler
        e.stopPropagation();

        const occurrenceDateObj = new Date(date);
        const existingNote = event.notes.find((n) => isSameDay(n.occurredAt, occurrenceDateObj));

        const onMutation = (): void => {
            void Promise.all([
                apiUtils.platoUser.query.events.getEventById.invalidate({
                    eventId: event.event_id,
                }),
                apiUtils.platoUser.query.events.getList.invalidate(),
                (async () => {
                    if (event.customer?.customerId) {
                        await apiUtils.platoUser.query.events.getUpcomingCustomerEvents.invalidate({
                            customerId: event.customer.customerId,
                        });
                    }
                })(),
            ]);
            void apiUtils.platoUser.query.events.getList.invalidate();
        };

        if (existingNote) {
            void DialogStateManager.show(EditNoteDialog, {
                noteId: existingNote.noteId,
                onSuccess: onMutation,
                onDelete: onMutation,
            });
        } else {
            void DialogStateManager.show(LogInteractionDialog, {
                event,
                customer: event.customer,
                analyticsSource: 'event_drawer',
                onSuccess: onMutation,
            });
        }
    };

    return (
        <Card
            ref={ref}
            // removes its own border if a button inside of it has been hovered
            className={cn(`relative p-3 gap-1 has-[button:hover]:border-neutral-100`, className)}
            onClick={handleClick}
        >
            {(event.customer || (event.start_time && event.end_time) || !!recurrenceWeeksCount) && (
                <div className="flex items-center justify-between">
                    {event.customer && (
                        <InfoTag icon={Store} key="customer_id" size="xs">
                            {event.customer.customerId}
                        </InfoTag>
                    )}
                    <div className="flex shrink-0 items-center gap-1.5 text-xs">
                        {event.start_time && event.end_time && (
                            <Text key="time" size="xs" color="neutral-500">
                                {t('card_time_format', {
                                    startTime: formatISOTimeForDisplay(event.start_time),
                                    endTime: formatISOTimeForDisplay(event.end_time),
                                })}
                            </Text>
                        )}
                        {!!recurrenceWeeksCount && <RefreshCwIcon className="size-[1em] text-neutral-600" />}
                    </div>
                </div>
            )}

            <div className="flex flex-col gap-1 shrink overflow-hidden">
                {event.customer && (
                    <Text weight="medium" color="neutral-900" className="truncate block">
                        {event.customer.customerName}
                    </Text>
                )}
                <Text
                    weight={event.customer ? 'normal' : 'medium'}
                    color={event.customer ? 'neutral-500' : 'neutral-900'}
                    className={event.customer ? undefined : 'truncate block'}
                >
                    {event.title ?? t('events__card_default_title')}
                </Text>
            </div>

            {event.address && (
                <InfoTag icon={MapPinIcon} key="address" className="truncate" size="xs">
                    {event.address}
                </InfoTag>
            )}
            {event.customer &&
                (event.customer.openTasksCount > 0 || event.customer.overdueTasksCount > 0) && (
                    <div className="flex items-center gap-1 text-neutral-500">
                        <div className="flex flex-wrap items-center gap-x-1.5">
                            {event.customer.openTasksCount > 0 && (
                                <InfoTag icon={CircleCheck} key="open_tasks" size="xs">
                                    {t('card_tasks_open', { count: event.customer.openTasksCount })}
                                </InfoTag>
                            )}
                            {event.customer.openTasksCount > 0 && event.customer.overdueTasksCount > 0 && (
                                <Text size="xs" color="neutral-500">
                                    ·
                                </Text>
                            )}
                            {event.customer.overdueTasksCount > 0 && (
                                <Text size="xs" color="red-600">
                                    {t('card_tasks_overdue', { count: event.customer.overdueTasksCount })}
                                </Text>
                            )}
                        </div>
                    </div>
                )}
            {showLogButton && (
                <EventLogButton
                    isLogged={event.notes.length > 0}
                    disabled={!event.customerId}
                    onClick={
                        isOwner
                            ? handleButtonClick
                            : (e) => {
                                  // don't bubble into the card's own click handler
                                  e.stopPropagation();
                                  handleClick();
                              }
                    }
                />
            )}
        </Card>
    );
});

EventCard.displayName = 'EventCard';
